import { useRef, useState } from "react";
import { FaPlay, FaPause, FaBackward, FaForward } from "react-icons/fa";

type VoicePlayerProps = {
  src: string;
};

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const VoicePlayer = ({ src }: VoicePlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      await audio.play();
      setPlaying(true);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const newTime = (Number(e.target.value) / 100) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipTime = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const nextTime = Math.min(Math.max(audio.currentTime + seconds, 0), duration || 0);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  return (
    <div className="w-full max-w-md rounded-[24px] bg-blue-700 px-2 py-2 text-white shadow-sm">
      <div className="flex flex-wrap items-center gap-y-1">
        <button
          type="button"
          onClick={() => skipTime(-10)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
          aria-label="Back 10 seconds"
        >
          <FaBackward size={16} />
        </button>

        <button
          type="button"
          onClick={togglePlay}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
          aria-label={playing ? "Pause voice message" : "Play voice message"}
        >
          {playing ? (
            <FaPause size={22} />
          ) : (
            <FaPlay size={22} className="ml-0.5" />
          )}
        </button>

        <button
          type="button"
          onClick={() => skipTime(10)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
          aria-label="Forward 10 seconds"
        >
          <FaForward size={16} />
        </button>

        <div className="relative min-w-0 flex-1">
          <div className="pointer-events-none flex h-8 items-center gap-[3px]">
            {Array.from({ length: 24 }).map((_, index) => {
              const heights = [
                10, 14, 18, 12, 20, 24, 14, 20, 26, 12, 22, 16, 10, 14, 22, 18, 12,
                16, 20, 14, 22, 18, 14, 18,
              ];

              const active = (index / 24) * 100 <= progress;

              return (
                <span
                  key={index}
                  className={`w-[3px] rounded-full ${
                    active ? "bg-white" : "bg-white/50"
                  }`}
                  style={{ height: `${heights[index]}px` }}
                />
              );
            })}
          </div>

          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={progress}
            onChange={handleSeek}
            className="absolute inset-0 h-8 w-full cursor-pointer opacity-0"
            aria-label="Seek voice message"
          />
        </div>

        <span className="pr-1 text-right text-xs font-semibold sm:ml-2 sm:w-auto sm:min-w-[88px] sm:text-sm">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
};
